import { isApiError } from 'container/models';

const ORIGINAL_ERROR_KEY = Symbol(
  'Original error this ApplicationError was derived from, using ApplicationError.FromError'
);

type DetailsType = Record<string | symbol, unknown>;

/**
 * ApplicationError is a custom error type that extends Error to allow it to include user-friendly error messages.
 */
export class ApplicationError extends Error {
  public userMessage: string;
  public articleCode?: number;
  public details: DetailsType;
  public showToDeveloper: boolean;

  constructor(
    /**
     * A message that will be shown to the user, most likely in an error toast.
     * Keep it short and to the point. "{Action} failed." is good. Only explain
     * *why* if there's something the user can do about it. If you think it's a
     * transient error, something that will probably work if they try again, tell
     * them that.
     */
    userMessage: string,
    /**
     * A message that will be shown to the developer. This should be as descriptive
     * as you want. Stacktraces will be available by virtue of this being an Error
     * subclass. Additional detail (like variables, etc) can be added to the details
     * property in the options.
     */
    message: string,
    /**
     * Additional options for the error.
     *
     * showToDeveloper: Log this error? If false, error-handling code will ignore
     * this error when processing it for developer consumption. Defaults true.
     *
     * details: Any extra data that might be useful. If there are concerns about
     * objects being mutated after being passed here, clone them with structuredClone()
     * first.
     */
    options?: {
      articleCode?: number;
      showToDeveloper?: boolean;
      details?: DetailsType;
    }
  ) {
    super(message);
    this.userMessage = userMessage;
    this.details = options?.details ?? {};
    this.showToDeveloper =
      options?.showToDeveloper === undefined ? true : options.showToDeveloper;
    this.articleCode = options?.articleCode;

    if (options?.details && ORIGINAL_ERROR_KEY in options.details) {
      Error.captureStackTrace(this, ApplicationError.FromError);
    } else {
      Error.captureStackTrace(this, ApplicationError);
    }
  }

  /**
   * Creates an ApplicationError from any kind of error object. Makes an effort
   * to preserve as much information as possible.
   *
   * Has specific cases for handling when `err` is:
   * - ApplicationError: just returns the original error
   * - Error: creates a new ApplicationError and copies all properties from the original
   * - ApiError: creates a new ApplicationError
   *
   * Everything else gets stored in the details property.
   */
  public static FromError(
    /** An object to convert into an ApplicationError. */
    err: unknown,
    /**
     * A message about this error to show to the user. Refer to `userMessage`
     * on the {@link ApplicationError} constructor for more details.
     */
    userMessage: string
  ): ApplicationError {
    // if it's already an ApplicationError, just return it
    if (isApplicationError(err)) return err;

    // if it's an Error, create a new ApplicationError from it, copying all properties from the original and storing the original as the detail
    if (err instanceof Error) {
      const newErr = new ApplicationError(userMessage, err.message, {
        details: {
          [ORIGINAL_ERROR_KEY]: err,
        },
      });
      Object.assign(newErr, err);
      return newErr;
    }

    // if it's an ApiError, we can extract specific values from it
    // @TODO - if single error in array, take userMessage from that. If multiple, store all but take userMessage from param instead.
    if (isApiError(err)) {
      const msgError = err.data[0];
      if (msgError && !err.clientErrorString)
        return new ApplicationError(
          msgError.description,
          msgError.description,
          {
            details: {
              [ORIGINAL_ERROR_KEY]: err,
            },
          }
        );

      return new ApplicationError(
        userMessage,
        `unknown API error: ${err.statusText}`,
        {
          details: {
            [ORIGINAL_ERROR_KEY]: err,
          },
        }
      );
    }

    // if it's an object, create a new ApplicationError from it, storing the original object as the details.
    if (typeof err === 'object' && err !== null) {
      return new ApplicationError(
        userMessage,
        'message' in err && typeof err.message === 'string'
          ? err.message
          : userMessage,
        {
          details: {
            [ORIGINAL_ERROR_KEY]: err,
          },
        }
      );
    }

    // otherwise, stringify it and return it
    return new ApplicationError(userMessage, String(err), {
      details: {
        [ORIGINAL_ERROR_KEY]: err,
      },
    });
  }

  public print() {
    console.groupCollapsed(
      `%cApplicationError: ${this.message} (${this.userMessage})`,
      'background-color: maroon; color: white;'
    );

    console.groupCollapsed('Context');

    const { [ORIGINAL_ERROR_KEY]: originalError, ...rest } = this.details;

    if (originalError) {
      console.groupCollapsed('Original error');

      console.debug(originalError);

      console.groupEnd();
    }

    if (Object.keys(rest).length > 0) console.debug('Other details:', rest);

    console.groupEnd();

    console.groupCollapsed('Stack');

    console.debug(this.stack);

    console.groupEnd();

    console.groupEnd();
  }
}

export function isApplicationError(obj: unknown): obj is ApplicationError {
  return (
    !!obj &&
    typeof obj === 'object' &&
    (obj instanceof ApplicationError || 'userMessage' in obj)
  );
}
